/*
 * Copyright (c) Kalaora 2022.
 * Author: fharold
 *
 * hilo-oauth-manager
 * UserService.ts
 */

import User from "../models/User";
import AuthenticationService, {AuthenticationWatcher} from "../tools/service/AuthenticationService";
import QueryFilter from "../tools/model/pagination/Filters";
import {Listener} from "../tools/service/Service";
import OAuthAPIService from "../tools/service/OAuthAPIService";
import OAuthHTTPService from "../tools/service/OAuthHTTPService";
import Device from "../models/Device";
import ProfileService from "./ProfileService";

export interface UserListener extends Listener<User> {
    currentUserHasBeenUpdated(user: User | undefined): void;
}

export interface UserFilters {
    size: number;
    page: number;
    mails?: string[];
}


export default class UserService extends OAuthAPIService<User, UserListener> implements AuthenticationWatcher {
    public static ROUTE_ME = '/oauth/me';
    public static SINGLE_ROUTE = '/oauth/user/{userId}';
    public static PASSWORD_CHANGE_ROUTE = '/oauth/user/{userId}/password';
    public static CLIENT_CHANGE_ROUTE = '/oauth/user/{userId}/clients';
    public static ROLES_CHANGE_ROUTE = '/oauth/user/{userId}/roles';
    public static PASSWORD_RESET_ROUTE = '/password/reset';
    public static LIST_ROUTE = '/oauth/users';
    public static LIST_DEVICES_ROUTE = '/oauth/client/{clientId}/devices';

    private _authenticationService: AuthenticationService;
    private _profileService: ProfileService;
    private _user?: User;
    public static id = "UserService"

    constructor(httpService: OAuthHTTPService, authenticationService: AuthenticationService, profileService: ProfileService) {
        super(httpService);

        this._authenticationService = authenticationService;
        this._profileService = profileService;
    }

    /**
     * authenticationWatcher
     * @param busy
     */
    public authenticationWatcher(busy: boolean): void {
    }

    /**
     * authenticationRefreshed
     */
    public authenticationRefreshed(): void {
        this.refreshCurrentUser();
    }

    /**
     * start
     */
    public start() {
        super.start();

        this._authenticationService.addListener(this);
    }

    /**
     * stop
     */
    public stop() {
        this._authenticationService.removeListener(this);

        super.stop();
    }

    /**
     * createUser
     * @return {Promise<User>}
     * @param dto
     */
    public async createUser(dto: Map<string, any>): Promise<User> {
        return await this.post(UserService.LIST_ROUTE, dto);
    }

    /**
     * notifyListeners
     */
    public notifyListeners = async () => {
        this.listeners.forEach((l) => l.currentUserHasBeenUpdated(this._user))
    };

    /**
     * findAll
     * @returns {Promise<User[]>}
     */
    public async findAll(): Promise<User[]> {
        const userFilters: QueryFilter<UserFilters> = new QueryFilter<UserFilters>({size: 10000, page: 0});

        return await this.getList<User, UserFilters>(UserService.LIST_ROUTE, userFilters);
    }

    /**
     * findByMail
     * @param email
     */
    public async findByMail(email: string): Promise<User | undefined> {
        const userFilters: QueryFilter<UserFilters> = new QueryFilter<UserFilters>({
            page: 0,
            size: 1000,
            mails: [encodeURI(email)]
        });

        const users = await this.getList<User, UserFilters>(UserService.LIST_ROUTE, userFilters);

        if (users.length === 1) {
            return users[0];
        }

        return undefined;
    }

    /**
     * updateUser
     * @return {Promise<User>}
     * @param dto
     * @param user
     */
    public async updateUser(user: User, dto: Map<string, any>): Promise<User> {
        const route = this.httpService.buildRoute(UserService.SINGLE_ROUTE, {
            userId: user.id
        });

        return await this.put(route, dto);
    }

    /**
     * resetPassword
     * @return {Promise<void>}
     * @param dto
     */
    public async resetPassword(dto: Map<string, any>): Promise<void> {
        try {
            return await this.post(UserService.PASSWORD_RESET_ROUTE, dto);
        } catch (e) {
            //crappy fix to avoid error displayal during reset. Server is sending back a whole html page.
        }
    }

    /**
     * setUserAccountActivation
     * @param user
     * @param dto
     * @returns {Promise<void>}
     */
    public async setActivation(user: User, dto: Map<string, any>): Promise<void> {
        const route = this.httpService.buildRoute(UserService.SINGLE_ROUTE + "/activate", {
            userId: user.id
        });

        return await this.post(route, dto);
    }

    /**
     * findById
     * @returns {Promise<User>}
     * @param id
     */
    public async findById(id: string): Promise<User> {
        const route = this.httpService.buildRoute(UserService.SINGLE_ROUTE, {
            userId: id
        });

        return await this.getOne<User>(route);
    }

    /**
     * user
     * @returns {User | undefined}
     */
    public get user(): User | undefined {
        return this._user;
    }

    /**
     * refreshCurrentUser
     * @returns {Promise<void>}
     */
    public async refreshCurrentUser() {

        try {
            this._user = await this.getOne(UserService.ROUTE_ME) as User;
            this.notifyListeners();
        } catch (e) {
            console.error("UserService._refreshCurrentUser:", e);
        }
    }

    /**
     * updateUserIdentity
     * @param dto
     * @returns {Promise<User>}
     * @param relatedUser
     */
    public async updateUserIdentity(relatedUser: User, dto: Map<string, any>): Promise<User> {
        const route = this.httpService.buildRoute(UserService.SINGLE_ROUTE, {
            userId: relatedUser.id
        });

        return await this.put(route, dto);
    }

    /**
     * updateUserPassword
     * @param dto
     * @returns {Promise<User>}
     * @param relatedUser
     */
    public async updateUserPassword(relatedUser: User, dto: Map<string, any>): Promise<User> {
        const route = this.httpService.buildRoute(UserService.PASSWORD_CHANGE_ROUTE, {
            userId: relatedUser.id
        });

        return await this.put(route, dto);
    }

    /**
     * updateClients
     * @param user
     * @param dto
     * @returns {Promise<void>}
     */
    public async updateClients(user: User, dto: Map<string, any>) {
        const route = this.httpService.buildRoute(UserService.CLIENT_CHANGE_ROUTE, {
            userId: user.id
        });

        return await this.post(route, dto);
    }

    /**
     * updateRoles
     * @param user
     * @param dto
     * @returns {Promise<void>}
     */
    public async updateRoles(user: User, dto: Map<string, any>) {
        const route = this.httpService.buildRoute(UserService.ROLES_CHANGE_ROUTE, {
            userId: user.id
        });

        return await this.post(route, dto);
    }

    public async findAllOrphansDevicesByClientID(clientId: string): Promise<Array<Device>> {
        const route = this.httpService.buildRoute(UserService.LIST_DEVICES_ROUTE, {
            clientId: clientId
        });

        const allDevices : Array<Device> = await this.getList(route);
        const existingProfiles = await this._profileService.findAll();
        //Exclude all existings profiles from "allDevices" list and display remaining members.
        return allDevices.filter(device => !existingProfiles.find(profile => profile.userId === device.uuid));
    }
}
